"use client";

import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
}

const FULL_HEX_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

function expandShortHex(hex: string): string {
  if (/^#[A-Fa-f0-9]{3}$/.test(hex)) {
    const [r, g, b] = hex.slice(1);
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return hex;
}

function normalizeHexInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

export default function ColorPicker({ value, onChange }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState(value);

  useEffect(() => {
    setHexInput(value);
  }, [value]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setHexInput(value);
    }
  };

  const commitHex = (raw: string) => {
    const normalized = normalizeHexInput(raw);
    if (!FULL_HEX_REGEX.test(normalized)) return false;
    const expanded = expandShortHex(normalized);
    setHexInput(expanded);
    onChange(expanded);
    return true;
  };

  const handleHexChange = (newValue: string) => {
    const normalized = normalizeHexInput(newValue);
    if (normalized.length > 7) return;

    const digits = normalized.startsWith("#") ? normalized.slice(1) : normalized;
    if (!/^[A-Fa-f0-9]{0,6}$/.test(digits)) return;

    setHexInput(normalized);
    if (FULL_HEX_REGEX.test(normalized)) {
      onChange(expandShortHex(normalized));
    }
  };

  const handleHexBlur = () => {
    if (!commitHex(hexInput)) {
      setHexInput(value);
    }
  };

  const handleColorInputChange = (newValue: string) => {
    setHexInput(newValue);
    onChange(newValue);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start text-left font-normal"
        >
          <div className="w-4 h-4 rounded mr-2" style={{ background: value }} />
          {value}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded border" style={{ background: value }} />
            <div className="flex-1">
              <p className="text-sm font-medium">HEX Color</p>
              <p className="text-lg font-mono">{value?.toUpperCase()}</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">HEX Value</label>
            <Input
              type="text"
              value={hexInput}
              onChange={(e) => handleHexChange(e.target.value)}
              onBlur={handleHexBlur}
              placeholder="#000000"
              className="font-mono"
              maxLength={7}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Color Picker</label>
            <Input
              type="color"
              value={expandShortHex(value)}
              onChange={(e) => handleColorInputChange(e.target.value)}
              className="w-full h-10 cursor-pointer"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
